import type { PaperWithRole } from "@/types/paper";
import type { PaperFilters } from "@/hooks/useFilteredPapers";

export interface CullSet {
  /** Papers safe to dismiss: hidden by the active filters, active (not
   *  dismissed either way), not flagged, not championed, not awaiting triage. */
  cullIds: string[];
  /** Hidden championed papers protected from the cull. */
  protectedChampioned: number;
  /** Hidden untriaged monitor arrivals protected from the cull. */
  protectedUntriaged: number;
}

const MONITOR_SOURCES = new Set(["monitor", "trawl_references"]);

function isDismissed(p: PaperWithRole): boolean {
  return (
    !!p.exclusion_reason ||
    (p.user_score_override != null && p.user_score_override <= 30)
  );
}

function isChampioned(p: PaperWithRole): boolean {
  return p.user_score_override != null && p.user_score_override >= 70;
}

function isUntriagedMonitor(p: PaperWithRole): boolean {
  return (
    MONITOR_SOURCES.has(p.added_source) && !p.triaged && !p.exclusion_reason
  );
}

/**
 * The filters whose complement the cull may dismiss. The angle tab is a
 * view, not a filter: papers outside the active angle belong to other angles,
 * so the cull must judge "hidden" against the filters WITHOUT the angle.
 */
export function cullFilters(filters: PaperFilters): PaperFilters {
  return { ...filters, angle: undefined };
}

/**
 * Compute the filter-complement cull set: papers in `basePapers` (the same
 * scoped list the visible table draws from — WS/camp scope already applied)
 * that the active filters are hiding and that are safe to bulk-dismiss.
 *
 * Protected classes are counted separately so the confirm dialog can report
 * what was kept: championed papers (a search-text filter shouldn't be able to
 * nuke the core set) and untriaged monitor arrivals (the inbox owns their
 * triage; culling them would also fake the >0→0 triage transition that gates
 * FD auto-refresh). Flagged papers are hidden by default independent of
 * filters, so they never count as "hidden by the filter".
 */
export function computeCullSet(
  basePapers: PaperWithRole[],
  displayedIds: ReadonlySet<string>,
): CullSet {
  const cullIds: string[] = [];
  let protectedChampioned = 0;
  let protectedUntriaged = 0;

  for (const p of basePapers) {
    if (displayedIds.has(p.id)) continue;
    if (isDismissed(p) || p.flagged_for_inbox) continue;
    if (isChampioned(p)) {
      protectedChampioned++;
      continue;
    }
    if (isUntriagedMonitor(p)) {
      protectedUntriaged++;
      continue;
    }
    cullIds.push(p.id);
  }

  return { cullIds, protectedChampioned, protectedUntriaged };
}

/**
 * Terse human-readable summary of the active filters, stamped into the
 * exclusion reason ("cull: score ≥ 40, 2020+") so the dismissed view shows
 * why each paper went and a whole cull stays identifiable afterwards.
 */
export function describeFilters(filters: PaperFilters): string {
  const parts: string[] = [];
  if (filters.searchText) parts.push(`search "${filters.searchText}"`);
  if (filters.minScore) parts.push(`score ≥ ${filters.minScore}`);
  if (filters.minCitations) parts.push(`≥ ${filters.minCitations} cites`);
  if (filters.earliestYear) parts.push(`${filters.earliestYear}+`);
  if (filters.isInfluential) parts.push("influential");
  if (filters.contributionType) parts.push(filters.contributionType);
  if (filters.argumentationRole) parts.push(filters.argumentationRole);
  if (filters.overrideFilter && filters.overrideFilter !== "active") {
    parts.push(
      filters.overrideFilter === "none"
        ? "no override"
        : filters.overrideFilter,
    );
  }
  if (filters.readingStatus) parts.push(filters.readingStatus);
  if (filters.tagIds && filters.tagIds.length > 0) parts.push("tagged");
  if (filters.hasEvidence) parts.push("has evidence");
  if (filters.hasStashed) parts.push("stashed");
  if (filters.sourceFilter) parts.push(filters.sourceFilter);
  if (filters.addedWithin) parts.push(`added ${filters.addedWithin}`);
  if (filters.viaSeed) parts.push("via seed");
  return parts.join(", ") || "filtered view";
}
